import React, { useState, useEffect, useCallback, useRef } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import SiteHeader from './components/SiteHeader';
import PivotTable from './components/PivotTable';
import './MapPage.css';
import API_CONFIG from './config';

const MapPage = () => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const googleMapRef = useRef(null);
  const googleMarkersRef = useRef([]);
  const searchBoxRef = useRef(null);
  const searchInputRef = useRef(null);
  const [googleApiKey, setGoogleApiKey] = useState('');
  const USE_GOOGLE_BASEMAP = !!googleApiKey;
  // Fetch Google Maps API Key the same way as homepage
  useEffect(() => {
    const fetchApiKey = async () => {
      try {
        const resp = await fetch(`${API_CONFIG.BASE_URL}/google-api-key`);
        if (resp.ok) {
          const data = await resp.json();
          if (data.success && data.apiKey) {
            setGoogleApiKey(data.apiKey);
            return;
          }
        }
      } catch (e) {
        // ignore and try fallback
      }

      // Fallback: extract key from embed proxy
      try {
        const embedResp = await fetch(`${API_CONFIG.BASE_URL}/google-maps-embed?location=Toronto`);
        if (embedResp.ok) {
          const html = await embedResp.text();
          const m = html.match(/key=([^&"']+)/);
          if (m && m[1]) {
            setGoogleApiKey(m[1]);
          }
        }
      } catch (e) {
        // leave key empty; MapLibre will be used
      }
    };
    fetchApiKey();
  }, []);

  
  // State management
  const [pivotData, setPivotData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter states (same as HomePage)
  const [selectedDateFilter, setSelectedDateFilter] = useState('all');
  const [bedroomFilter, setBedroomFilter] = useState('all');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [propertyTypeFilter, setPropertyTypeFilter] = useState('all');
  
  // Map-specific state
  const [selectedNeighborhood, setSelectedNeighborhood] = useState(null);
  const markersRef = useRef([]);
  const neighborhoodsRef = useRef(null);
  const displayClusterRef = useRef(null);
  const isUpdatingMarkersRef = useRef(false);
  const pivotDataRef = useRef(null);

  // Fetch neighborhood-specific pivot data (like homepage does)
  const fetchNeighborhoodPivotData = useCallback(async (neighborhoodName) => {
    try {
      console.log(`Fetching pivot data for neighborhood: ${neighborhoodName}`);
      
      const searchParams = new URLSearchParams();
      searchParams.append('city', 'Toronto');
      searchParams.append('neighborhood', neighborhoodName);
      if (selectedDateFilter !== 'all') searchParams.append('date_filter', selectedDateFilter);
      if (bedroomFilter !== 'all') searchParams.append('bedroom_filter', bedroomFilter);
      if (minPrice) searchParams.append('min_price', minPrice);
      if (maxPrice) searchParams.append('max_price', maxPrice);
      if (propertyTypeFilter !== 'all') searchParams.append('property_type', propertyTypeFilter);
      
      const url = `https://nk2lgsxkwf.execute-api.us-east-1.amazonaws.com/prod/listing-details-pivot?${searchParams}`;
      console.log('Fetching neighborhood pivot data:', url);
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (response.ok && data.success) {
        console.log(`Got pivot data for ${neighborhoodName}:`, data.data);
        return data.data;
      } else {
        console.error('Failed to fetch neighborhood pivot data:', data);
        return null;
      }
    } catch (error) {
      console.error('Error fetching neighborhood pivot data:', error);
      return null;
    }
  }, [selectedDateFilter, bedroomFilter, minPrice, maxPrice, propertyTypeFilter]);

  // Fetch and combine data for multiple neighborhoods
  const fetchAndCombineMultipleNeighborhoods = useCallback(async (neighborhoodNames) => {
    try {
      console.log(`Fetching data for ${neighborhoodNames.length} neighborhoods:`, neighborhoodNames);
      
      const promises = neighborhoodNames.map(name => fetchNeighborhoodPivotData(name));
      const results = await Promise.all(promises);
      
      const validResults = results.filter(result => result && result.pivot_table);
      
      if (validResults.length === 0) {
        console.error('No valid data for any neighborhoods in cluster');
        return null;
      }
      
      console.log(`Got valid data for ${validResults.length}/${neighborhoodNames.length} neighborhoods`);
      
      const combinedPivotTable = {};
      let combinedTotalListings = 0;
      
      validResults.forEach((result, index) => {
        console.log(`Combining data from neighborhood ${index + 1}:`, Object.keys(result.pivot_table));
        combinedTotalListings += result.total_listings || 0;
        
        Object.entries(result.pivot_table).forEach(([propertyType, typeData]) => {
          if (!combinedPivotTable[propertyType]) {
            combinedPivotTable[propertyType] = {
              label: typeData.label,
              property_type: typeData.property_type,
              bedrooms: {}
            };
          }
          
          Object.entries(typeData.bedrooms || {}).forEach(([bedroom, bedroomData]) => {
            if (!combinedPivotTable[propertyType].bedrooms[bedroom]) {
              combinedPivotTable[propertyType].bedrooms[bedroom] = {
                count: 0,
                total_rent: 0,
                min_rent: Infinity,
                max_rent: 0
              };
            }
            
            const existing = combinedPivotTable[propertyType].bedrooms[bedroom];
            existing.count += bedroomData.count || 0;
            existing.total_rent += (bedroomData.average_rent || 0) * (bedroomData.count || 0);
            existing.min_rent = Math.min(existing.min_rent, bedroomData.min_rent || Infinity);
            existing.max_rent = Math.max(existing.max_rent, bedroomData.max_rent || 0);
          });
        });
      });
      
      Object.values(combinedPivotTable).forEach(typeData => {
        Object.values(typeData.bedrooms).forEach(bedroomData => {
          if (bedroomData.count > 0) {
            bedroomData.average_rent = bedroomData.total_rent / bedroomData.count;
            if (bedroomData.min_rent === Infinity) bedroomData.min_rent = bedroomData.average_rent;
          }
          delete bedroomData.total_rent;
        });
      });
      
      console.log('Combined pivot table:', combinedPivotTable);
      console.log('Combined total listings:', combinedTotalListings);
      
      return {
        pivot_table: combinedPivotTable,
        total_listings: combinedTotalListings,
        property_types: Object.keys(combinedPivotTable),
        bedrooms: ['0', '1', '2', '3', '4', '5+'],
        data_source: 'combined_neighborhoods'
      };
      
    } catch (error) {
      console.error('Error combining neighborhood data:', error);
      return null;
    }
  }, [fetchNeighborhoodPivotData]);

  // Fetch pivot data by neighborhood IDs (used for Google markers)
  const fetchPivotByNeighborhoodIds = useCallback(async (neighborhoodIds, cityName) => {
    try {
      const ids = Array.isArray(neighborhoodIds) ? neighborhoodIds : [neighborhoodIds];
      const params = new URLSearchParams();
      if (ids.length > 0) params.append('neighborhood_id', ids.join(','));
      if (cityName) params.append('city', cityName);
      if (selectedDateFilter !== 'all') params.append('date_filter', selectedDateFilter);
      if (bedroomFilter !== 'all') params.append('bedroom_filter', bedroomFilter);
      if (minPrice) params.append('min_price', minPrice);
      if (maxPrice) params.append('max_price', maxPrice);
      if (propertyTypeFilter !== 'all') params.append('property_type', propertyTypeFilter);
      const url = `https://nk2lgsxkwf.execute-api.us-east-1.amazonaws.com/prod/listing-details-pivot?${params.toString()}`;
      const resp = await fetch(url);
      const data = await resp.json();
      if (resp.ok && data.success) return data.data;
    } catch (e) {
      console.error('Error fetching pivot by neighborhood IDs:', e);
    }
    return null;
  }, [selectedDateFilter, bedroomFilter, minPrice, maxPrice, propertyTypeFilter]);

  const handleOverlayCellClick = useCallback((propertyType, bedroomCount, bedroomData) => {
    setSelectedNeighborhood(null);
    
    const searchParams = new URLSearchParams({
      property_type: propertyType,
      bedrooms: bedroomCount,
      count: bedroomData.count || '0',
      avg_rent: Math.round(bedroomData.average_rent) || '0',
      min_rent: Math.round(bedroomData.min_rent) || '0',
      max_rent: Math.round(bedroomData.max_rent) || '0',
      user_role: 'tenant',
      sources: 'realtor,facebook,kijiji'
    });

    const neighborhood = selectedNeighborhood?.appliedFilters?.neighborhood;
    if (neighborhood) {
      searchParams.append('city', 'Toronto');
      searchParams.append('area', neighborhood);
      searchParams.append('location_display', `${neighborhood}, Toronto`);
    }

    window.location.href = `/chat?${searchParams.toString()}`;
  }, [selectedNeighborhood]);

  const clearMarkers = useCallback(() => {
    try {
      console.log(`🧹 Clearing ${markersRef.current ? markersRef.current.length : 0} existing markers`);
      if (markersRef.current && markersRef.current.length > 0) {
        markersRef.current.forEach(m => {
          try {
            if (m && m.remove) m.remove();
          } catch (e) {
            console.error('Error removing marker:', e);
          }
        });
      }
    } catch (e) {
      console.error('Error in clearMarkers:', e);
    }
    markersRef.current = [];
    console.log('✅ Markers cleared');
  }, []);

  const fetchAndDisplayRealNeighborhoodData = useCallback(async () => {
    if (!map.current) return;

    try {
      clearMarkers();
      const neighborhoodResponse = await fetch('https://nk2lgsxkwf.execute-api.us-east-1.amazonaws.com/prod/neighborhood-coordinates');
      const neighborhoodData = await neighborhoodResponse.json();

      if (!neighborhoodData.success || !neighborhoodData.data.neighborhoods) {
        console.error('Failed to fetch neighborhood data');
        return;
      }

      const torontoNeighborhoods = Object.values(neighborhoodData.data.neighborhoods)
        .filter(n => n.city === 'Toronto')
        .slice(0, 20);

      console.log(`Found ${torontoNeighborhoods.length} Toronto neighborhoods with real coordinates`);
      const newMarkers = [];

      torontoNeighborhoods.forEach((neighborhood) => {
        const { name, city, latitude, longitude } = neighborhood;
        const mockListingCount = Math.floor(Math.random() * 500) + 50;
        const mockAvgRent = Math.floor(Math.random() * 2000) + 1500;
        
        const markerElement = document.createElement('div');
        markerElement.className = 'map-marker';
        markerElement.innerHTML = `
          <div class="marker-content">
            <div class="marker-count">${mockListingCount}</div>
          </div>
        `;
        
        const popupContent = `
          <div class="map-popup">
            <h3>📍 ${name}</h3>
            <div class="popup-location">📍 ${city}, Ontario</div>
            <div class="popup-stats">
              <div class="stat">
                <span class="stat-label">Listings:</span>
                <span class="stat-value">${mockListingCount}</span>
              </div>
              <div class="stat">
                <span class="stat-label">Avg Rent:</span>
                <span class="stat-value">$${mockAvgRent.toLocaleString()}</span>
              </div>
              <div class="stat">
                <span class="stat-label">Coordinates:</span>
                <span class="stat-value">${latitude.toFixed(4)}, ${longitude.toFixed(4)}</span>
              </div>
            </div>
            <div class="popup-note">
              <small>Real neighborhood with database coordinates</small>
            </div>
          </div>
        `;

        const popup = new maplibregl.Popup({
          offset: 25,
          closeButton: true,
          closeOnClick: false,
          maxWidth: '320px'
        }).setHTML(popupContent);

        const marker = new maplibregl.Marker({ element: markerElement, anchor: 'center' })
          .setLngLat([longitude, latitude])
          .addTo(map.current);

        marker.setPopup(popup);
        newMarkers.push(marker);
      });

      markersRef.current = newMarkers;
      console.log(`Created ${newMarkers.length} markers with REAL neighborhood coordinates`);

    } catch (error) {
      console.error('Error fetching real neighborhood data:', error);
    }
  }, [clearMarkers]);

  const tryCoordinateEndpointOrFallback = useCallback(async () => {
    if (!map.current) return;

    try {
      clearMarkers();
      const currentFilters = {
        date_filter: selectedDateFilter,
        bedroom_filter: bedroomFilter,
        min_price: minPrice,
        max_price: maxPrice,
        property_type: propertyTypeFilter
      };
      
      const searchParams = new URLSearchParams();
      if (currentFilters.date_filter && currentFilters.date_filter !== 'all') {
        searchParams.append('days_filter', currentFilters.date_filter);
      }
      if (currentFilters.bedroom_filter && currentFilters.bedroom_filter !== 'all') {
        searchParams.append('bedrooms', currentFilters.bedroom_filter);
      }
      if (currentFilters.min_price) {
        searchParams.append('min_price', currentFilters.min_price);
      }
      if (currentFilters.max_price) {
        searchParams.append('max_price', currentFilters.max_price);
      }
      if (currentFilters.property_type && currentFilters.property_type !== 'all') {
        searchParams.append('property_type', currentFilters.property_type);
      }

      const coordUrl = `https://nk2lgsxkwf.execute-api.us-east-1.amazonaws.com/prod/pivot-with-coordinates?${searchParams}`;
      console.log('Map fetching coordinate data with same filters as pivot table:', Object.fromEntries(searchParams));
      
      const coordResponse = await fetch(coordUrl);
      
      if (coordResponse.ok) {
        const coordData = await coordResponse.json();
        if (coordData.success && coordData.data.neighborhoods) {
          console.log('Using coordinate endpoint with real listing data');
          neighborhoodsRef.current = coordData.data.neighborhoods;
          if (displayClusterRef.current) {
            await displayClusterRef.current(neighborhoodsRef.current);
          }
          return;
        }
      }

      console.log('Coordinate endpoint failed, using neighborhood fallback');
      await fetchAndDisplayRealNeighborhoodData();
      
    } catch (error) {
      console.error('Error in coordinate endpoint:', error);
      await fetchAndDisplayRealNeighborhoodData();
    }
  }, [clearMarkers, selectedDateFilter, bedroomFilter, minPrice, maxPrice, propertyTypeFilter, fetchAndDisplayRealNeighborhoodData]);

  const processFilteredPivotDataForMap = useCallback(async (data) => {
    if (!data || !data.pivot_table) {
      console.log('No pivot table data to process for map, falling back.');
      await tryCoordinateEndpointOrFallback();
      return;
    }

    try {
      console.log('Processing filtered pivot data for map markers...');
      
      const neighborhoodsFromPivot = {};
      
      Object.entries(data.pivot_table).forEach(([key, neighborhoodData]) => {
          if (neighborhoodData && typeof neighborhoodData.total_listings === 'number') {
              neighborhoodsFromPivot[key] = {
                  name: key,
                  city: neighborhoodData.city || 'Toronto',
                  latitude: neighborhoodData.latitude,
                  longitude: neighborhoodData.longitude,
                  total_listings: neighborhoodData.total_listings,
                  avg_rent: neighborhoodData.average_rent || 0,
                  property_types: neighborhoodData.property_types
              };
          }
      });

      console.log(`Extracted ${Object.keys(neighborhoodsFromPivot).length} neighborhoods from pivot table`);

      if (Object.keys(neighborhoodsFromPivot).length > 0) {
        neighborhoodsRef.current = neighborhoodsFromPivot;
        if (displayClusterRef.current) {
          await displayClusterRef.current(neighborhoodsFromPivot);
        }
      } else {
        console.warn('Could not extract any neighborhoods from pivot table, falling back.');
        await tryCoordinateEndpointOrFallback();
      }
    } catch (error) {
      console.error('Error processing pivot data for map:', error);
      await tryCoordinateEndpointOrFallback();
    }
  }, [tryCoordinateEndpointOrFallback]);

  // Fetch pivot data (same API as HomePage)
  const fetchPivotData = useCallback(async (filters = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      const searchParams = new URLSearchParams();
      
      if (filters.date_filter && filters.date_filter !== 'all') {
        searchParams.append('days_filter', filters.date_filter);
      }
      if (filters.bedroom_filter && filters.bedroom_filter !== 'all') {
        searchParams.append('bedrooms', filters.bedroom_filter);
      }
      if (filters.min_price && filters.min_price !== '') {
        searchParams.append('min_price', filters.min_price);
      }
      if (filters.max_price && filters.max_price !== '') {
        searchParams.append('max_price', filters.max_price);
      }
      if (filters.property_type && filters.property_type !== 'all') {
        searchParams.append('property_type', filters.property_type);
      }
      
      const url = `https://nk2lgsxkwf.execute-api.us-east-1.amazonaws.com/prod/listing-details-pivot?${searchParams}`;
      console.log('Map fetching same pivot data as table with filters:', Object.fromEntries(searchParams));

      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        setPivotData(data.data);
        pivotDataRef.current = data.data;
        setError(null);
        if (data.data.pivot_table) {
          console.log('Map using same pivot table data as the table below');
          await processFilteredPivotDataForMap(data.data);
        } else {
          console.log('No pivot table in response, falling back to coordinate endpoint');
          await tryCoordinateEndpointOrFallback();
        }
      } else {
        setError(data.message || 'Failed to fetch data');
      }
    } catch (error) {
      console.error('Error fetching pivot data:', error);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, [tryCoordinateEndpointOrFallback, processFilteredPivotDataForMap]);

  const clusterOverlappingMarkers = useCallback((neighborhoods, zoom) => {
    if (!map.current || neighborhoods.length === 0) return neighborhoods;
    
    const baseSize = 20;
    const zoomFactor = Math.pow(1.4, zoom - 10);
    const markerSize = Math.max(10, Math.min(80, baseSize * zoomFactor));
    const pixelThreshold = markerSize * 2.5;
    
    const toPoint = (lng, lat) => map.current.project([lng, lat]);
    const distancePx = (a, b) => {
      const pa = toPoint(a.longitude, a.latitude);
      const pb = toPoint(b.longitude, b.latitude);
      const dx = pa.x - pb.x;
      const dy = pa.y - pb.y;
      return Math.sqrt(dx * dx + dy * dy);
    };

    const clusters = [];
    const used = new Set();

    const sortedNeighborhoods = neighborhoods.map((n, index) => ({...n, originalIndex: index}))
      .sort((a, b) => b.total_listings - a.total_listings);

    sortedNeighborhoods.forEach((n, i) => {
      if (used.has(n.originalIndex)) return;
      
      const cluster = { ...n, neighborhoods: [n], clustered_count: 1 };

      sortedNeighborhoods.forEach((m, j) => {
        if (i === j || used.has(m.originalIndex)) return;
        const distance = distancePx(n, m);
        if (distance <= pixelThreshold) {
          cluster.total_listings += m.total_listings;
          cluster.avg_rent = Math.round((cluster.avg_rent * cluster.clustered_count + m.avg_rent) / (cluster.clustered_count + 1));
          cluster.neighborhoods.push(m);
          cluster.clustered_count++;
          used.add(m.originalIndex);
          console.log(`Clustering ${m.name} into ${n.name} (distance: ${distance.toFixed(1)}px)`);
        }
      });

      used.add(n.originalIndex);
      clusters.push(cluster);
    });

    console.log(`Clustering: ${neighborhoods.length} neighborhoods → ${clusters.length} clusters at zoom ${zoom.toFixed(1)} (threshold: ${pixelThreshold.toFixed(1)}px)`);
    
    const multiClusters = clusters.filter(c => c.clustered_count > 1);
    if (multiClusters.length > 0) {
      console.log(`Found ${multiClusters.length} aggregated clusters:`, multiClusters.map(c => `${c.name} (+${c.clustered_count-1})`));
    }
    
    return clusters;
  }, []);

  const displayNeighborhoodsWithClustering = useCallback(async (neighborhoods) => {
    if (!map.current || isUpdatingMarkersRef.current) return;
    
    isUpdatingMarkersRef.current = true;
    clearMarkers();

    const currentZoom = map.current.getZoom();
    const newMarkers = [];
    
    const neighborhoodArray = Object.values(neighborhoods);
    
    const torontoNeighborhoods = neighborhoodArray
      .filter(n => n.city === 'Toronto' || n.city === 'North York' || n.city === 'Scarborough' || n.city === 'Etobicoke')
      .filter(n => n.total_listings > 0)
      .sort((a, b) => b.total_listings - a.total_listings);

    console.log(`Found ${torontoNeighborhoods.length} Toronto neighborhoods with listings`);
    console.log('Top 5 Toronto neighborhoods:', torontoNeighborhoods.slice(0, 5).map(n => ({name: n.name, city: n.city, listings: n.total_listings, lat: n.latitude, lng: n.longitude})));

    let clusteredNeighborhoods;
    if (currentZoom >= 14) {
      clusteredNeighborhoods = torontoNeighborhoods.slice(0, 50);
    } else if (currentZoom >= 12) {
      const displayNeighborhoods = torontoNeighborhoods.slice(0, 30);
      clusteredNeighborhoods = clusterOverlappingMarkers(displayNeighborhoods, currentZoom);
    } else {
      const displayNeighborhoods = torontoNeighborhoods.slice(0, 20);
      clusteredNeighborhoods = clusterOverlappingMarkers(displayNeighborhoods, currentZoom);
    }

    clusteredNeighborhoods.forEach((cluster) => {
      const { latitude, longitude, name, city, total_listings, property_types, neighborhoods: clusterNeighborhoods } = cluster;
      
      console.log(`Creating marker: ${name}, ${city} - ${total_listings} listings at [${longitude}, ${latitude}]`);
      console.log(`Marker coordinates: lng=${longitude}, lat=${latitude}`);
      
      const baseSize = 20;
      const zoomFactor = Math.pow(1.4, currentZoom - 10);
      const markerSize = Math.max(10, Math.min(80, baseSize * zoomFactor));
      const fontSize = Math.max(12, Math.min(32, markerSize * 0.5));
      
      const propertyTypeSum = property_types ? 
        Object.values(property_types).reduce((sum, type) => sum + (type.count || 0), 0) : 0;
      
      if (property_types && propertyTypeSum !== total_listings) {
        console.log(`📊 ${name}: Total ${total_listings} listings, property types show ${propertyTypeSum} (some types may be missing from breakdown)`);
      }
      
      const markerElement = document.createElement('div');
      markerElement.className = 'map-marker';
      markerElement.style.cursor = 'pointer';
      markerElement.style.left = `${-markerSize/2}px`;
      markerElement.style.top = `${-markerSize/2}px`;
      markerElement.innerHTML = `
        <div class="marker-content" style="width: ${markerSize}px; height: ${markerSize}px; font-size: ${fontSize}px;">
          <div class="marker-count">${total_listings}</div>
        </div>
      `;

      markerElement.addEventListener('click', () => {
        console.log('Marker clicked for:', name, city);
        console.log('Cluster neighborhoods:', clusterNeighborhoods);
        console.log('Full pivot data:', pivotDataRef.current?.pivot_table);
        
        let neighborhoodNames;
        if (clusterNeighborhoods && clusterNeighborhoods.length > 0) {
          neighborhoodNames = clusterNeighborhoods.map(n => 
            typeof n === 'string' ? n : (n.name || n)
          );
        } else {
          neighborhoodNames = [name];
        }
        
        console.log('Extracted neighborhood names for filtering:', neighborhoodNames);
        
        if (neighborhoodNames.length > 1) {
          console.log(`Cluster detected with ${neighborhoodNames.length} neighborhoods:`, neighborhoodNames);
          fetchAndCombineMultipleNeighborhoods(neighborhoodNames).then(combinedPivotData => {
            if (combinedPivotData) {
              console.log('Successfully combined cluster data:', combinedPivotData);
              setSelectedNeighborhood({
                showPivotTable: true,
                pivotData: combinedPivotData,
                selectedNeighborhood: name,
                selectedCity: city,
                clusterNeighborhoods: clusterNeighborhoods,
                appliedFilters: {
                  date_filter: selectedDateFilter,
                  bedroom_filter: bedroomFilter,
                  min_price: minPrice,
                  max_price: maxPrice,
                  property_type: propertyTypeFilter,
                  neighborhood: neighborhoodNames.join(', ')
                },
                currentZoom,
                isCluster: true,
                clusterNote: `Combined data for ${neighborhoodNames.length} neighborhoods: ${neighborhoodNames.join(', ')}`
              });
            } else {
              console.error('Failed to fetch cluster data, using fallback');
              setSelectedNeighborhood({
                showPivotTable: true,
                pivotData: pivotDataRef.current,
                selectedNeighborhood: name,
                selectedCity: city,
                clusterNeighborhoods: clusterNeighborhoods,
                appliedFilters: {
                  date_filter: selectedDateFilter,
                  bedroom_filter: bedroomFilter,
                  min_price: minPrice,
                  max_price: maxPrice,
                  property_type: propertyTypeFilter,
                  neighborhood: neighborhoodNames.join(', ')
                },
                currentZoom,
                isCluster: true,
                clusterNote: `This cluster contains ${neighborhoodNames.length} neighborhoods. Showing aggregated data for all Toronto with your filters.`
              });
            }
          });
        } else {
          fetchNeighborhoodPivotData(neighborhoodNames[0]).then(neighborhoodPivotData => {
            if (neighborhoodPivotData) {
              console.log('Successfully fetched neighborhood data:', neighborhoodPivotData);
              setSelectedNeighborhood({
                showPivotTable: true,
                pivotData: neighborhoodPivotData,
                selectedNeighborhood: name,
                selectedCity: city,
                clusterNeighborhoods: clusterNeighborhoods,
                appliedFilters: {
                  date_filter: selectedDateFilter,
                  bedroom_filter: bedroomFilter,
                  min_price: minPrice,
                  max_price: maxPrice,
                  property_type: propertyTypeFilter,
                  neighborhood: neighborhoodNames.join(', ')
                },
                currentZoom
              });
            } else {
              console.error('Failed to fetch neighborhood data, using fallback');
              setSelectedNeighborhood({
                showPivotTable: true,
                pivotData: pivotDataRef.current,
                selectedNeighborhood: name,
                selectedCity: city,
                clusterNeighborhoods: clusterNeighborhoods,
                appliedFilters: {
                  date_filter: selectedDateFilter,
                  bedroom_filter: bedroomFilter,
                  min_price: minPrice,
                  max_price: maxPrice,
                  property_type: propertyTypeFilter,
                  neighborhood: neighborhoodNames.join(', ')
                },
                currentZoom,
                isAggregated: true
              });
            }
          });
        }
      });

      const marker = new maplibregl.Marker({ 
        element: markerElement, 
        anchor: 'center'
      })
        .setLngLat([longitude, latitude])
        .addTo(map.current);
        
      console.log(`Marker for ${name} positioned at [${longitude}, ${latitude}] - should match popup location`);
      newMarkers.push(marker);
    });

    markersRef.current = newMarkers;
    console.log(`Created ${newMarkers.length} clustered markers (zoom: ${currentZoom.toFixed(1)})`);
    
    if (newMarkers.length > 0 && !neighborhoodsRef.current._boundsSet) {
      const bounds = new maplibregl.LngLatBounds();
      clusteredNeighborhoods.forEach(cluster => {
        bounds.extend([cluster.longitude, cluster.latitude]);
      });
      map.current.fitBounds(bounds, { 
        padding: { top: 50, bottom: 50, left: 50, right: 50 },
        maxZoom: 13 
      });
      neighborhoodsRef.current._boundsSet = true;
    }
    
    isUpdatingMarkersRef.current = false;
  }, [clearMarkers, clusterOverlappingMarkers, fetchAndCombineMultipleNeighborhoods, fetchNeighborhoodPivotData, selectedDateFilter, bedroomFilter, minPrice, maxPrice, propertyTypeFilter]);

  useEffect(() => {
    displayClusterRef.current = displayNeighborhoodsWithClustering;
    return () => { displayClusterRef.current = null; };
  }, [displayNeighborhoodsWithClustering]);

  useEffect(() => {
    if (!mapContainer.current) return;

    if (USE_GOOGLE_BASEMAP) {
      // Load Google Maps JS API and show a simple basemap for visual comparison
      const loadGoogle = () => {
        if (window.google && window.google.maps) return Promise.resolve();
        if (window.__gmapsLoadingPromise) return window.__gmapsLoadingPromise;
        window.__gmapsLoadingPromise = new Promise((resolve, reject) => {
          let script = document.getElementById('google-maps-script');
          if (!script) {
            script = document.createElement('script');
            script.id = 'google-maps-script';
            script.src = `https://maps.googleapis.com/maps/api/js?key=${googleApiKey}&libraries=places`;
            script.async = true;
            script.defer = true;
            script.setAttribute('loading', 'async');
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
          } else {
            script.addEventListener('load', resolve, { once: true });
            script.addEventListener('error', reject, { once: true });
          }
        });
        return window.__gmapsLoadingPromise;
      };

      loadGoogle().then(() => {
        googleMapRef.current = new window.google.maps.Map(mapContainer.current, {
          center: { lat: 43.6532, lng: -79.3832 },
          zoom: 11,
          mapTypeId: 'roadmap',
          disableDefaultUI: false,
        });
        console.log('Google Maps basemap initialized for preview');

        // Add search box functionality
        const createSearchBox = () => {
          // Create search input element
          const input = document.createElement('input');
          input.type = 'text';
          input.placeholder = 'Search for address, landmark, or postal code...';
          input.style.cssText = `
            width: 300px;
            height: 40px;
            margin: 10px;
            padding: 0 12px;
            border: 1px solid #ccc;
            border-radius: 4px;
            font-size: 14px;
            box-shadow: 0 2px 6px rgba(0,0,0,0.3);
            background: white;
            outline: none;
          `;
          searchInputRef.current = input;

          // Create search box
          searchBoxRef.current = new window.google.maps.places.SearchBox(input);
          
          // Add search box to map
          googleMapRef.current.controls[window.google.maps.ControlPosition.TOP_LEFT].push(input);

          // Listen for places changed
          searchBoxRef.current.addListener('places_changed', () => {
            const places = searchBoxRef.current.getPlaces();
            if (places.length === 0) return;

            const place = places[0];
            if (!place.geometry || !place.geometry.location) {
              console.warn('Place has no geometry');
              return;
            }

            // Center map on the selected place
            googleMapRef.current.setCenter(place.geometry.location);
            
            // Adjust zoom based on place type
            if (place.geometry.viewport) {
              googleMapRef.current.fitBounds(place.geometry.viewport);
            } else {
              // Default zoom for specific locations
              googleMapRef.current.setZoom(15);
            }

            console.log(`Map recentered to: ${place.formatted_address || place.name}`);
          });

          // Bias search results to current map bounds
          googleMapRef.current.addListener('bounds_changed', () => {
            if (searchBoxRef.current && googleMapRef.current) {
              searchBoxRef.current.setBounds(googleMapRef.current.getBounds());
            }
          });
        };

        const clearGoogleMarkers = () => {
          if (!googleMarkersRef.current) return;
          googleMarkersRef.current.forEach(m => m.setMap(null));
          googleMarkersRef.current = [];
        };

        const buildCircleSymbol = (count, zoom) => {
          const base = 12; // px radius baseline
          const scale = Math.max(10, Math.min(28, base + (zoom - 10) * 2));
          return {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale,
            fillColor: '#f7931e',
            fillOpacity: 0.95,
            strokeColor: '#ffffff',
            strokeWeight: 2
          };
        };

        let debounceTimer;
        const fetchAndRenderMarkers = async () => {
          try {
            const mapObj = googleMapRef.current;
            if (!mapObj) return;
            const zoom = mapObj.getZoom();
            const bounds = mapObj.getBounds();
            if (!bounds) return;
            const ne = bounds.getNorthEast();
            const sw = bounds.getSouthWest();
            const params = new URLSearchParams({
              zoom: String(zoom),
              south: String(sw.lat()),
              north: String(ne.lat()),
              west: String(sw.lng()),
              east: String(ne.lng())
            });
            const url = `${API_CONFIG.BASE_URL}/map-markers?${params.toString()}`;
            const resp = await fetch(url);
            const data = await resp.json();
            if (!resp.ok || !data.success) return;
            clearGoogleMarkers();
            const info = new window.google.maps.InfoWindow();
            const markers = Array.isArray(data.data) ? data.data : (data.data && data.data.markers ? data.data.markers : []);
            markers.forEach(marker => {
              const position = { lat: Number(marker.latitude), lng: Number(marker.longitude) };
              const count = Number(
                marker.quantity ?? marker.total_listings ?? marker.count ?? 0
              );
              const m = new window.google.maps.Marker({
                position,
                map: mapObj,
                icon: buildCircleSymbol(count, zoom),
                label: {
                  text: String(count),
                  color: '#ffffff',
                  fontWeight: '700'
                },
                title: marker.marker_name
              });
              m.addListener('click', async () => {
                // Parse neighborhood IDs if present
                let ids = [];
                try {
                  if (Array.isArray(marker.neighborhood_ids)) ids = marker.neighborhood_ids;
                  else if (typeof marker.neighborhood_ids === 'string' && marker.neighborhood_ids.trim().length > 0) ids = JSON.parse(marker.neighborhood_ids);
                } catch (_) {}

                // Fallback to showing info window if no IDs
                if (!ids || ids.length === 0) {
                  info.setContent(`<div style="font-weight:600;margin-bottom:4px;max-width:240px">${marker.marker_name}</div><div>${count} listings</div>`);
                  info.open({ anchor: m, map: mapObj, shouldFocus: false });
                  return;
                }

                const pivot = await fetchPivotByNeighborhoodIds(ids, marker.city_name || 'Toronto');
                if (pivot) {
                  setSelectedNeighborhood({
                    showPivotTable: true,
                    pivotData: pivot,
                    selectedNeighborhood: ids.length > 1 ? `Neighborhood IDs: ${ids.join(', ')}` : marker.marker_name,
                    selectedCity: marker.city_name || 'Toronto',
                    clusterNeighborhoods: ids,
                    appliedFilters: {
                      date_filter: selectedDateFilter,
                      bedroom_filter: bedroomFilter,
                      min_price: minPrice,
                      max_price: maxPrice,
                      property_type: propertyTypeFilter,
                      neighborhood: marker.marker_name
                    }
                  });
                } else {
                  info.setContent(`<div style="font-weight:600;margin-bottom:4px;max-width:240px">${marker.marker_name}</div><div>${count} listings</div>`);
                  info.open({ anchor: m, map: mapObj, shouldFocus: false });
                }
              });
              googleMarkersRef.current.push(m);
            });
          } catch (e) {
            console.warn('Failed to render Google markers', e);
          }
        };

        const debouncedUpdate = () => {
          clearTimeout(debounceTimer);
          debounceTimer = setTimeout(fetchAndRenderMarkers, 300);
        };

        // Initial and on interactions
        window.google.maps.event.addListenerOnce(googleMapRef.current, 'idle', fetchAndRenderMarkers);
        googleMapRef.current.addListener('idle', debouncedUpdate);
        
        // Initialize search box
        createSearchBox();
      }).catch((e) => {
        console.error('Failed to load Google Maps API', e);
        setError('Failed to load Google Maps basemap');
      });

      return () => {
        // No explicit cleanup needed for Google Maps object
        googleMapRef.current = null;
        searchBoxRef.current = null;
        searchInputRef.current = null;
      };
    }

    // Default MapLibre path
    if (map.current) return;
    try {
      map.current = new maplibregl.Map({
        container: mapContainer.current,
        style: {
          version: 8,
          sources: {
            'carto-voyager-nolabels': {
              type: 'raster',
              tiles: [
                'https://a.basemaps.cartocdn.com/rastertiles/voyager_nolabels/{z}/{x}/{y}{r}.png',
                'https://b.basemaps.cartocdn.com/rastertiles/voyager_nolabels/{z}/{x}/{y}{r}.png',
                'https://c.basemaps.cartocdn.com/rastertiles/voyager_nolabels/{z}/{x}/{y}{r}.png',
                'https://d.basemaps.cartocdn.com/rastertiles/voyager_nolabels/{z}/{x}/{y}{r}.png'
              ],
              tileSize: 256,
              attribution: '© OpenStreetMap contributors © CARTO'
            }
          },
          layers: [
            {
              id: 'carto-voyager-nolabels',
              type: 'raster',
              source: 'carto-voyager-nolabels',
              minzoom: 0,
              maxzoom: 19
            }
          ]
        },
        center: [-79.3832, 43.6532],
        zoom: 11
      });

      map.current.addControl(new maplibregl.NavigationControl(), 'top-right');

      let handleZoomEnd = () => {
        if (!neighborhoodsRef.current || !displayClusterRef.current) return;
        const currentZoom = map.current.getZoom();
        console.log(`Zoom changed to ${currentZoom.toFixed(1)}, re-clustering markers`);
        displayClusterRef.current(neighborhoodsRef.current);
      };
      map.current.on('zoomend', handleZoomEnd);

      console.log('Map initialized successfully');
    } catch (error) {
      console.error('Error initializing map:', error);
      setError('Failed to initialize map. Please refresh the page.');
    }

    return () => {
      if (map.current) {
        try { map.current.off('zoomend'); } catch (e) {}
        clearMarkers();
        map.current.remove();
        map.current = null;
      }
    };
  }, [clearMarkers, USE_GOOGLE_BASEMAP, googleApiKey, fetchPivotByNeighborhoodIds, selectedDateFilter, bedroomFilter, minPrice, maxPrice, propertyTypeFilter]);

  useEffect(() => {
    if (!USE_GOOGLE_BASEMAP) {
      fetchPivotData();
    }
  }, [fetchPivotData, USE_GOOGLE_BASEMAP]);

  useEffect(() => {
    if (USE_GOOGLE_BASEMAP) return;
    const filters = {
      date_filter: selectedDateFilter,
      bedroom_filter: bedroomFilter,
      min_price: minPrice,
      max_price: maxPrice,
      property_type: propertyTypeFilter
    };
    fetchPivotData(filters);
  }, [selectedDateFilter, bedroomFilter, minPrice, maxPrice, propertyTypeFilter, fetchPivotData, USE_GOOGLE_BASEMAP]);

  return (
    <div className="map-page">
      <SiteHeader />
      
      <div className="map-container-wrapper">
        <div className="map-filters">
          <h2>🗺️ Rental Map</h2>
          
          <div className="filter-group">
            <label>📅 Listed Time:</label>
            <select 
              value={selectedDateFilter} 
              onChange={(e) => setSelectedDateFilter(e.target.value)}
              className="filter-select"
              disabled={loading}
            >
              <option value="all">All Time</option>
              <option value="1">Last 1 day</option>
              <option value="3">Last 3 days</option>
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
            </select>
          </div>

          <div className="filter-group">
            <label>🛏️ Bedrooms:</label>
            <select 
              value={bedroomFilter} 
              onChange={(e) => setBedroomFilter(e.target.value)}
              className="filter-select"
              disabled={loading}
            >
              <option value="all">All Bedrooms</option>
              <option value="0">Studio</option>
              <option value="1">1 Bedroom</option>
              <option value="2">2 Bedrooms</option>
              <option value="3">3 Bedrooms</option>
              <option value="4">4 Bedrooms</option>
              <option value="5">5+ Bedrooms</option>
            </select>
          </div>

          <div className="filter-group">
            <label>🏠 Property Type:</label>
            <select 
              value={propertyTypeFilter} 
              onChange={(e) => setPropertyTypeFilter(e.target.value)}
              className="filter-select"
              disabled={loading}
            >
              <option value="all">All Types</option>
              <option value="Apartment">Apartment</option>
              <option value="Apartment (With Parking)">Apartment (With Parking)</option>
              <option value="Apartment (No Parking)">Apartment (No Parking)</option>
              <option value="House">House</option>
              <option value="Basement">Basement</option>
              <option value="Row / Townhouse">Row / Townhouse</option>
            </select>
          </div>

          <div className="filter-group price-range">
            <label>💰 Price Range:</label>
            <div className="price-inputs">
              <input
                type="number"
                placeholder="Min"
                value={minPrice}
                onChange={(e) => setMinPrice(e.target.value)}
                className="price-input"
                disabled={loading}
              />
              <span>-</span>
              <input
                type="number"
                placeholder="Max"
                value={maxPrice}
                onChange={(e) => setMaxPrice(e.target.value)}
                className="price-input"
                disabled={loading}
              />
            </div>
          </div>

          {pivotData && (
            <div className="map-summary">
              <div className="summary-stats">
                <div className="stat">
                  <span className="stat-number">{pivotData.total_neighborhoods || Object.keys(pivotData.neighborhoods || pivotData.pivot_table || {}).length}</span>
                  <span className="stat-label">Areas</span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="map-wrapper">
          {loading && (
            <div className="map-loading-overlay">
              <div className="loading-spinner"></div>
              <p>Loading map data...</p>
            </div>
          )}
          
          {error && (
            <div className="map-error-overlay">
              <h3>Error Loading Map</h3>
              <p>{error}</p>
              <button onClick={() => fetchPivotData()} className="retry-button">
                Try Again
              </button>
            </div>
          )}
          
          <div ref={mapContainer} className="map-container" />
          
          {selectedNeighborhood && selectedNeighborhood.showPivotTable && (
            <div className="neighborhood-overlay" onClick={() => setSelectedNeighborhood(null)}>
              <div className="neighborhood-overlay-content pivot-table-overlay" onClick={(e) => e.stopPropagation()}>
                <button className="overlay-close" onClick={() => setSelectedNeighborhood(null)}>×</button>
                
                <div style={{textAlign: 'center', marginBottom: '20px'}}>
                  <h2 style={{fontSize: '24px', fontWeight: '600', color: '#333', margin: '0', lineHeight: '1.2'}}>
                    {selectedNeighborhood.appliedFilters?.neighborhood || selectedNeighborhood.selectedNeighborhood}
                  </h2>
                </div>
                
                <PivotTable 
                  pivotData={selectedNeighborhood.pivotData}
                  selectedCity="Toronto"
                  selectedArea={selectedNeighborhood.appliedFilters?.neighborhood || ''}
                  onCellClick={handleOverlayCellClick}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MapPage;